/* eslint-disable no-nested-ternary */
import React from 'react';
import shortid from 'shortid';
import { cloneDeep, filter, includes, get, map, isEmpty, find, forEach, every, noop, size, findIndex } from 'lodash';

// import default view
import { cagFilterOverlay as CagFilterOverlayView } from './cagFilterOverlay.view';
import { CagFilterOverlayService } from 'services/cagFilterOverlay';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import * as errorOverlay from 'utils/errorOverlay';

/**
 * @type {function}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @param {object} Component - A React component
 * @example
 * import { CagFilterOverlay } from 'modules/cagFilterOverlay/overlay';
 *
 * // render
 * <CagFilterOverlay propName='propValue'/>
 */
export class CagFilterOverlay extends React.Component {
    constructor( props ) {
        super( props );

        // bind methods
        this.handleActionClick = this.handleActionClick.bind( this );
        this.handleAllSelection = this.handleAllSelection.bind( this );
        this.handleOptionClick = this.handleOptionClick.bind( this );
        this.handleCheckboxClick = this.handleCheckboxClick.bind( this );
        this.handleSearchQueryChange = this.handleSearchQueryChange.bind( this );
        this.handleTypeaheadSelection = this.handleTypeaheadSelection.bind( this );
        this.fetchAccountsList = this.fetchAccountsList.bind( this );
        this.fetchGroupOptions = this.fetchGroupOptions.bind( this );

         // create service instance
         this.cagFilterOverlayService = new CagFilterOverlayService();

         const carrierList = this.getInitialCarriers( this.props.carriers, this.props.value );

        // component state
        this.state = {
            carriers: carrierList, // selected carrier options
            isAllSelected: 0 === size( carrierList ),
            accountSearchQuery: '',
            groupSearchQuery: '',
            accountSearchId: [],
            groupSearchId: []
        };
    }

    /******************************************/

    /**
     * @desc Get initial carriers from the props
     */
    getInitialCarriers( carriers = [], value = [] ) {
        log.debug( 'CagFilterOverlay.getInitialCarriers()' );

        // clone carriers
        const _carriers = cloneDeep( carriers );

        // process carrier options
        forEach( _carriers, _carrier => {
            
            const carrierItem = find( value, { value: _carrier.value } );
            const accountItems = get( carrierItem, 'accounts', [] );

            _carrier.isSelected = isEmpty( _carrier.options ) ? ! isEmpty( carrierItem ) : ( isEmpty( accountItems ) ? false : ( size( accountItems ) === size( _carrier.options ) ? true : null ) );
            _carrier.isActive = false !== _carrier.isSelected; // if not `false` then active
            _carrier.isFetching = false;

            // process accounts options
            forEach( _carrier.options, _account => {
                const accountItem = find( accountItems, { value: _account.value } );
                const groupItems = get( accountItem, 'groups', [] );
                
                _account.isSelected = isEmpty( _account.options ) ? ! isEmpty( accountItem ) : ( isEmpty( groupItems ) ? false : ( size( groupItems ) === size( _account.options ) ? true : null ) );
                _account.isActive = false !== _account.isSelected; // if not `false` then active
                _account.isFetching = false;

                // process group options
                forEach( _account.options, _group => {
                    const groupItem = find( groupItems, { value: _group.value } );

                    _group.isSelected = ! isEmpty( groupItem );
                    _group.isActive = false !== _group.isSelected; // if not `false` then active
                } );
            } );
        } );

        // return processed carriers
        return _carriers;
    }

    /**
     * @desc When `All` checkbox is clicked
     */
    handleAllSelection() {
        log.debug( 'CagFilterOverlay.handleAllSelection()' );

        // update state
        this.setState( {
            isAllSelected: ! this.state.isAllSelected,
        } );
    }

    /**
     * @desc When carrier options are chosen from the typeahead search
     */
    handleTypeaheadSelection( { options = [], selected = [] } = {} ) {
        log.debug( 'CagFilterOverlay.handleTypeaheadSelection()' );

        // clone state
        const state = cloneDeep( this.state );

        // get selected options
        const selectedOptions = filter( options, option => {
            return includes( selected, option.value );
        } );

        // push only unique options
        forEach( selectedOptions, selectedOption => {
            if( isEmpty( find( state.carriers, { value: selectedOption.value } ) ) ) {
                const _selectedOption = cloneDeep( selectedOption );

                // update selected option state recursively
                _selectedOption.isSelected = false;
                _selectedOption.isActive = false;

                forEach( _selectedOption.options, _account => {
                    _account.isSelected = false;
                    _account.isActive = false;

                    forEach( _account.options, _group => {
                        _group.isSelected = false;
                        _account.isActive = false;
                    } );
                } );

                // add to `state.carriers`
                state.carriers.unshift( _selectedOption );
            }
        } );

        // update state
        this.setState( state );
    }

    /**
     * @desc When CAG option is clicked, modify the state
     */
    async handleOptionClick( { step, option } = {} ) {
        log.debug( 'CagFilterOverlay.handleOptionClick()' );

        // clone state
        const state = cloneDeep( this.state );

        // handle each step separately
        if( 'carrier' === step ) {
            
            // find selected options
            const carrier = find( state.carriers, { value: option.value } );

            if( 0 === size( get( carrier, 'options' ) ) ) {
                const carrierIndex = findIndex( state.carriers, { value: option.value } );
                state.carriers[ carrierIndex ].isFetching = true;

                this.setState( state, async () => {

                    const options = await this.fetchAccountsList( { carrier: option.value } );
                    state.carriers[ carrierIndex ].options = options;
                    state.carriers[ carrierIndex ].isFetching = false;
                    state.carriers[ carrierIndex ].isSelected = false;
                    state.carriers[ carrierIndex ].isActive = false;

                    // update selected state
                    if( ! isEmpty( carrier ) && false === carrier.isSelected ) {
                        carrier.isActive = ! carrier.isActive;
                    }

                    // update state
                    this.setState( state );

                } );

               
            } else {

                // update selected state
                if( ! isEmpty( carrier ) && false === carrier.isSelected ) {
                    carrier.isActive = ! carrier.isActive;
                }

                // update state
                this.setState( state );
            }

        } else if( 'account' === step ) {
            
            // find selected options
            const carrier = find( state.carriers, { value: option.carrier.value } );
            const account = find( carrier.options, { value: option.value } );

            if( 0 === size( get( account, 'options' ) ) ) {

                const carrierIndex = findIndex( state.carriers, { value: option.carrier.value } );
                const accountIndex = findIndex( carrier.options, { value: option.value } );
                state.carriers[ carrierIndex ].options[ accountIndex ].isFetching = true;

                this.setState( state, async () => {

                    const options = await this.fetchGroupOptions( { carrier: option.carrier.value, account: option.value } );

                    state.carriers[ carrierIndex ].options[ accountIndex ].options = options;
                    state.carriers[ carrierIndex ].options[ accountIndex ].isSelected = false;
                    state.carriers[ carrierIndex ].options[ accountIndex ].isActive = false;
                    state.carriers[ carrierIndex ].options[ accountIndex ].isFetching = false;

                    // update selected state
                    if( ! isEmpty( account ) && false === account.isSelected ) {
                        account.isActive = ! account.isActive;
                    }

                    // update state
                    this.setState( state );

                } );
            } else {

                // update selected state
                if( ! isEmpty( account ) && false === account.isSelected ) {
                    account.isActive = ! account.isActive;
                }

                // update state
                this.setState( state );
            }

        }
        
    }

    /**
     * fetch account list
     */
    async fetchAccountsList( { carrier } ) {
        log.debug( 'CagFilterOverlay.fetchAccountsList()' );
            
        // cancel ongoing request
        this.cagFilterOverlayService.cancel();

        // get status and service data
        const { status, data } = await this.cagFilterOverlayService.fetchAccountsList( {
            url: get( this.props, 'steps.account.url' ),
            data: {
                data: {
                    carrier
                },
                context: {}
            }
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            errorOverlay.open();
            return [];
        } else {

            const options = cloneDeep( get( data, 'options', '' ) );
            forEach( options, option => {
                option.isActive = false;
                option.isFetching = false;
                option.isSelected = false;
            } );

            return options;
        }
    }

    /**
     * fetch group list
     */
    async fetchGroupOptions( { carrier, account } ) {
        log.debug( 'CagFilterOverlay.fetchGroupOptions()' );
            
        // cancel ongoing request
        this.cagFilterOverlayService.cancel();

        // get status and service data
        const { status, data } = await this.cagFilterOverlayService.fetchGroupOptions( {
            url: get( this.props, 'steps.group.url' ),
            data: {
                data: {
                    carrier,
                    account
                },
                context: {}
            }
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            errorOverlay.open();
            return [];
        } else {

            const options = cloneDeep( get( data, 'options', '' ) );
            forEach( options, option => {
                option.isActive = false;
                option.isFetching = false;
                option.isSelected = false;
            } );

            return options;
        }

    }
 
    /**
     * @desc When CAG option checkbox is clicked, modify the state
     */
    handleCheckboxClick( { step, option } = {} ) {
        log.debug( 'CagFilterOverlay.handleCheckboxClick()' );

        // clone state
        const state = cloneDeep( this.state );

        // handle each step separately
        if( 'carrier' === step ) {
            
            // find selected options
            const carrier = find( state.carriers, { value: option.value } );

            // update selected state
            if( ! isEmpty( carrier ) ) {
                carrier.isSelected = carrier.isSelected ? false : true;
                carrier.isActive = carrier.isSelected;

                // select children recursively
                if( ! isEmpty( carrier.options ) ) {
                    forEach( carrier.options, _account => {
                        _account.isSelected = carrier.isSelected;
                        _account.isActive = carrier.isActive;
    
                        if( ! isEmpty( _account.options ) ) {
                            forEach( _account.options, _group => {
                                _group.isSelected = carrier.isSelected;
                                _group.isActive = carrier.isActive;
                            } );
                        }
                    } );
                }
            }

        } else if( 'account' === step ) {
            
            // find selected options
            const carrier = find( state.carriers, { value: option.carrier.value } );
            const account = find( carrier.options, { value: option.value } );

            // update selected state
            if( ! isEmpty( account ) ) {
                account.isSelected = account.isSelected ? false : true;
                account.isActive = account.isSelected;

                // select children recursively
                if( ! isEmpty( account.options ) ) {
                    forEach( account.options, _group => {
                        _group.isSelected = account.isSelected;
                        _group.isActive = account.isActive;
                    } );
                }

                // set selected state of parent (carrier)
                if( every( carrier.options, _account => true === _account.isSelected ) ) {
                    carrier.isSelected = true;
                } else if( every( carrier.options, _account => false === _account.isSelected ) ) {
                    carrier.isSelected = false;
                } else {
                    carrier.isSelected = null; // intermediate state
                }
            }


        } else if( 'group' === step ) {
            
            // find selected option
            const carrier = find( state.carriers, { value: option.carrier.value } );
            const account = find( carrier.options, { value: option.account.value } );
            const group = find( account.options, { value: option.value } );

            // update selected state
            if( ! isEmpty( group ) ) {
                group.isSelected = group.isSelected ? false : true;
                group.isActive = group.isSelected;

                // set selected state of parent (account)
                if( every( account.options, _group => true === _group.isSelected ) ) {
                    account.isSelected = true;
                } else if( every( account.options, _group => false === _group.isSelected ) ) {
                    account.isSelected = false;
                } else {
                    account.isSelected = null; // intermediate state
                }

                // set selected state of grand-parent (carrier)
                if( every( carrier.options, _account => true === _account.isSelected ) ) {
                    carrier.isSelected = true;
                } else if( every( carrier.options, _account => false === _account.isSelected ) ) {
                    carrier.isSelected = false;
                } else {
                    carrier.isSelected = null; // intermediate state
                }
            }
        }

        // update state
        this.setState( state );

    }

    /**
     * @desc When account and group search query changes
     */
    handleSearchQueryChange( { step, searchQuery = '' } = {} ) {
        log.debug( 'CagFilterOverlay.handleSearchQueryChange()' );

        if( 'account' === step ) {
            this.setState( {
                accountSearchQuery: searchQuery,
            } );
        } else if( 'group' === step ) {
            this.setState( {
                groupSearchQuery: searchQuery,
            } );
        }
    }

    /**
     * @desc Calculate new state based on option selection
     */
    getFormattedState() {
        log.debug( 'CagFilterOverlay.getFormattedState()' );

        // clone state
        const state = cloneDeep( this.state );

        // carrier, account and group options
        const carriers = [];
        const accounts = [];
        const groups = [];

        // process carrier options
        forEach( state.carriers, carrier => {

            // push into carriers
            carriers.push( {
                ...carrier,
                id: shortid.generate(),
                type: 'carrier',
                isLeaf: isEmpty( carrier.options ),
            } );

            // handle accounts array
            if( carrier.isActive && ! isEmpty( carrier.options ) ) {

                // carrier specific accounts
                const carrierAccounts = {
                    id: shortid.generate(),
                    title: carrier.title,
                    options: [],
                };

                // process account options
                forEach( carrier.options, account => {
    
                    // push into `carrierAccounts`
                    carrierAccounts.options.push( {
                        ...account,
                        id: shortid.generate(),
                        type: 'account',
                        isLeaf: isEmpty( account.options ),
                        carrier: carrier,
                    } );
        
                    // handle groups array
                    if( account.isActive && ! isEmpty( account.options ) ) {

                        // account specific groups
                        const accountGroups = {
                            id: shortid.generate(),
                            title: account.title,
                            options: [],
                        };

                        // process group options
                        forEach( account.options, group => {
        
                            // push into `accountGroups`
                            accountGroups.options.push( {
                                ...group,
                                id: shortid.generate(),
                                type: 'group',
                                isLeaf: true,
                                carrier: carrier,
                                account: account,
                            } );
                            
                        } );

                        // push `accountGroups` to `groups`
                        groups.push( accountGroups );
                    }

                } );

                // push `carrierAccounts` to `accounts`
                accounts.push( carrierAccounts );

            }

        } );
        

        return {
            steps: {
                carrier: {
                    ...this.props.steps.carrier,
                    options: carriers,
                },
                account: {
                    ...this.props.steps.account,
                    options: accounts,
                },
                group: {
                    ...this.props.steps.group,
                    options: groups,
                },
            }
        };
    }

    /**
     * @desc When cancel or apply action is clicked
     */
    handleActionClick( action = {} ) {
        log.debug( 'CagFilterOverlay.handleActionClick()' );

        // handle `cancel` action click
        if( 'cancel' === action.name ) {
            return this.props.closeOverlay();
        }

        // when `apply` action is clicked
        if( 'apply' === action.name ) {

            // check if `all` option is selected
            if( this.state.isAllSelected ) {
                this.props.closeOverlay();

                // return this.props.onApply( {
                //     values: 'ALL', // return `ALL` value
                // } );

                return this.props.onApply( {
                    values: [], // return `ALL` value
                } );
            }

            // filter selected options only
            const values = map( filter( this.state.carriers, _carrier => false !== _carrier.isSelected ), _carrier => {
                return {
                    value: _carrier.value,
                    accounts: map( filter( _carrier.options, _account => false !== _account.isSelected ), _account => {
                        return {
                            value: _account.value,
                            groups: map( filter( _account.options, _group => false !== _group.isSelected ), _group => {
                                return {
                                    value: _group.value,
                                };
                            } ),
                        };
                    } ),
                };
            } );

            // close overlay
            this.props.closeOverlay();

            // send `values` in the callback
            this.props.onApply( {
                values
            } );
        }
    }

    /******************************************/

    render() {

        // disable apply button
        const disableApply = ! this.state.isAllSelected && every( this.state.carriers, _carrier => false === _carrier.isSelected );

        return (
            <CagFilterOverlayView
                { ...this.props }
                { ...this.getFormattedState() }
                accountSearchQuery={ this.state.accountSearchQuery }
                groupSearchQuery={ this.state.groupSearchQuery }
                isAllSelected={ this.state.isAllSelected }
                disableApply={ disableApply }
                onActionClick={ this.handleActionClick }
                onAllSelection={ this.handleAllSelection }
                onOptionClick={ this.handleOptionClick }
                onCheckboxClick={ this.handleCheckboxClick }
                onSearchQueryChange={ this.handleSearchQueryChange }
                onTypeaheadSelection={ this.handleTypeaheadSelection }
            />
        );
    }
}

// set display name
CagFilterOverlay.displayName = 'CagFilterOverlay';

// default props
CagFilterOverlay.defaultProps = {
    closeOverlay: noop,
    onApply: noop,
    value: {},
    carriers: [],
    steps: {},
    actions: [],
};

// prop types
CagFilterOverlay.propTypes = {};
